import { create } from 'zustand'
import { apiClient } from '@/lib/api-client'
import type { Workspace, WorkspaceListItem, Document, DocumentListItem } from '@/types/workspace'

interface WorkspaceState {
  // Workspace data
  workspaces: WorkspaceListItem[]
  currentWorkspace: Workspace | null
  
  // Document data
  documents: DocumentListItem[]
  currentDocument: Document | null
  
  // Loading states
  loading: {
    workspaces: boolean
    workspace: boolean
    documents: boolean
    document: boolean
  }
  
  // Error states
  errors: {
    workspaces: string | null
    workspace: string | null
    documents: string | null
    document: string | null
  }
  
  // Tab management
  tabs: Array<{
    id: string
    type: 'all-docs' | 'document'
    title: string
    workspaceId?: string
    documentId?: string
  }>
  activeTabId: string | null
  
  // Actions
  fetchWorkspaces: () => Promise<void>
  setCurrentWorkspace: (workspaceId: string) => Promise<void>
  fetchDocuments: (workspaceId: string) => Promise<void>
  createDocument: (workspaceId: string, title?: string) => Promise<Document>
  openDocument: (document: DocumentListItem) => void
  
  // Tab actions
  addTab: (tab: { type: 'all-docs' | 'document', title: string, workspaceId?: string, documentId?: string }) => void
  closeTab: (tabId: string) => void
  setActiveTab: (tabId: string) => void
  
  // Clear functions
  clearErrors: () => void
  reset: () => void
}

export const useWorkspaceStore = create<WorkspaceState>((set, get) => ({
  // Initial state
  workspaces: [],
  currentWorkspace: null,
  documents: [],
  currentDocument: null,
  
  loading: {
    workspaces: false,
    workspace: false,
    documents: false,
    document: false,
  },
  
  errors: {
    workspaces: null,
    workspace: null,
    documents: null,
    document: null,
  },
  
  tabs: [],
  activeTabId: null,
  
  // Actions
  fetchWorkspaces: async () => {
    set(state => ({
      loading: { ...state.loading, workspaces: true },
      errors: { ...state.errors, workspaces: null }
    }))
    
    try {
      const response = await apiClient.getWorkspaces()
      set({ workspaces: response.workspaces })
      
      // If no current workspace is set and there are workspaces, set the first one
      const { currentWorkspace } = get()
      if (!currentWorkspace && response.workspaces.length > 0) {
        await get().setCurrentWorkspace(response.workspaces[0]!.id)
      }
    } catch (error: any) {
      set(state => ({
        errors: { ...state.errors, workspaces: error.message || 'Failed to fetch workspaces' }
      }))
    } finally {
      set(state => ({
        loading: { ...state.loading, workspaces: false }
      }))
    }
  },
  
  setCurrentWorkspace: async (workspaceId: string) => {
    set(state => ({
      loading: { ...state.loading, workspace: true },
      errors: { ...state.errors, workspace: null }
    }))
    
    try {
      const workspace = await apiClient.getWorkspace(workspaceId)
      set({ currentWorkspace: workspace })
      
      // Fetch documents for this workspace
      await get().fetchDocuments(workspaceId)
      
      // Add "All Docs" tab if not present
      const { tabs, activeTabId } = get()
      const allDocsTab = tabs.find(tab => tab.type === 'all-docs' && tab.workspaceId === workspaceId)
      
      if (!allDocsTab) {
        const newTab = {
          id: `all-docs-${workspaceId}`,
          type: 'all-docs' as const,
          title: 'All Docs',
          workspaceId
        }
        
        set(state => ({ 
          tabs: [...state.tabs, newTab],
          activeTabId: activeTabId || newTab.id
        }))
      }
      
    } catch (error: any) {
      set(state => ({
        errors: { ...state.errors, workspace: error.message || 'Failed to fetch workspace' }
      }))
    } finally {
      set(state => ({
        loading: { ...state.loading, workspace: false }
      }))
    }
  },
  
  fetchDocuments: async (workspaceId: string) => {
    set(state => ({
      loading: { ...state.loading, documents: true },
      errors: { ...state.errors, documents: null }
    }))
    
    try {
      const response = await apiClient.getDocuments({ workspaceId })
      set({ documents: response.documents })
    } catch (error: any) {
      set(state => ({
        errors: { ...state.errors, documents: error.message || 'Failed to fetch documents' }
      }))
    } finally {
      set(state => ({
        loading: { ...state.loading, documents: false }
      }))
    }
  },
  
  createDocument: async (workspaceId: string, title?: string) => {
    try {
      const document = await apiClient.createDocument({
        workspaceId,
        title: title || 'Untitled'
      })
      
      // Refresh documents list
      await get().fetchDocuments(workspaceId)
      
      return document
    } catch (error: any) {
      throw error
    }
  },
  
  openDocument: (document: DocumentListItem) => {
    const { tabs } = get()
    const existingTab = tabs.find(tab => tab.documentId === document.id)
    
    if (existingTab) {
      // Tab already exists, just switch to it
      set({ activeTabId: existingTab.id })
    } else {
      // Create new tab
      const newTab = {
        id: `doc-${document.id}`,
        type: 'document' as const,
        title: document.title,
        workspaceId: document.workspaceId,
        documentId: document.id
      }
      
      set(state => ({
        tabs: [...state.tabs, newTab],
        activeTabId: newTab.id
      }))
    }
  },
  
  // Tab actions
  addTab: (tab) => {
    const tabId = `${tab.type}-${Date.now()}`
    const newTab = { ...tab, id: tabId }
    
    set(state => ({
      tabs: [...state.tabs, newTab],
      activeTabId: tabId
    }))
  },
  
  closeTab: (tabId: string) => {
    set(state => {
      const newTabs = state.tabs.filter(tab => tab.id !== tabId)
      let newActiveTabId = state.activeTabId
      
      // If we're closing the active tab, switch to the previous or next tab
      if (state.activeTabId === tabId) {
        if (newTabs.length > 0) {
          const closedIndex = state.tabs.findIndex(tab => tab.id === tabId)
          const nextIndex = Math.min(closedIndex, newTabs.length - 1)
          newActiveTabId = newTabs[nextIndex]?.id || null
        } else {
          newActiveTabId = null
        }
      }
      
      return {
        tabs: newTabs,
        activeTabId: newActiveTabId
      }
    })
  },
  
  setActiveTab: (tabId: string) => {
    set({ activeTabId: tabId })
  },
  
  // Clear functions
  clearErrors: () => {
    set({
      errors: {
        workspaces: null,
        workspace: null,
        documents: null,
        document: null,
      }
    })
  },
  
  reset: () => {
    set({
      workspaces: [],
      currentWorkspace: null,
      documents: [],
      currentDocument: null,
      loading: {
        workspaces: false,
        workspace: false,
        documents: false,
        document: false,
      },
      errors: {
        workspaces: null,
        workspace: null,
        documents: null,
        document: null,
      },
      tabs: [],
      activeTabId: null,
    })
  },
}))